const DEFAULT_HEADERS = {
  Accept: 'application/json',
};

// Vite injects env vars via import.meta.env in the browser build.
// In Node (playground scripts / one-off CLI tests), import.meta.env is undefined,
// so we also honor process.env.
const ENV_SOURCE = (typeof import.meta !== 'undefined' && import.meta.env)
  ? import.meta.env
  : (typeof process !== 'undefined' ? process.env : {});

const API_BASE_URL = String(
  ENV_SOURCE?.VITE_API_URL
  ?? ENV_SOURCE?.VITE_API_BASE_URL
  ?? ''
).replace(/\/+$/, '');

const REQUEST_ID_HEADERS = [
  'x-request-id',
  'x-requestid',
  'x-correlation-id',
  'x-amzn-trace-id',
];

const FRONTEND_REPO = 'Nuosis/NBNA';

function generateCorrelationId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // Fall back to the timestamp-based correlation id below.
  }
  return `cid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

let authTokenProvider = null;
let authRefreshHandler = null;
let refreshPromise = null;

export function setAuthTokenProvider(provider) {
  authTokenProvider = provider;
}

export function setAuthRefreshHandler(handler) {
  authRefreshHandler = handler;
}

export class ApiError extends Error {
  constructor(message, { status, code, details, requestId, data } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
    this.data = data;
  }
}

function isAbsoluteUrl(value) {
  return /^https?:\/\//i.test(value);
}

function joinUrl(baseUrl, path) {
  if (!baseUrl || isAbsoluteUrl(path)) return path;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
}

function buildQuery(params) {
  if (!params || typeof params !== 'object') return '';
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, String(item)));
      return;
    }
    searchParams.set(key, String(value));
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

function getRequestId(headers) {
  for (const header of REQUEST_ID_HEADERS) {
    const value = headers.get(header);
    if (value) return value;
  }
  return null;
}

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

async function parseResponseBody(response) {
  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();
  if (text === '') return null;

  const isJson = contentType.includes('application/json');
  if (!isJson) return text;

  try {
    return JSON.parse(text);
  } catch (error) {
    return text;
  }
}

function extractErrorPayload(data) {
  if (!data || typeof data !== 'object') return {};
  if (data.error && typeof data.error === 'object') return data.error;
  if (typeof data.error === 'string') return { message: data.error };
  return data;
}

function buildApiError({ status, statusText, data, requestId }) {
  const payload = extractErrorPayload(data);
  const validationDetail = Array.isArray(payload.detail)
    ? payload.detail.map((item) => item?.msg).filter(Boolean).join('; ')
    : null;
  const message = payload.message
    || (typeof payload.detail === 'string' ? payload.detail : validationDetail)
    || statusText
    || 'Request failed';
  return new ApiError(message, {
    status,
    code: payload.code,
    details: payload.details,
    requestId,
    data,
  });
}

async function resolveAuthToken({ authToken, skipAuth }) {
  if (skipAuth) return null;
  if (authToken !== undefined) return authToken;
  if (!authTokenProvider) return null;
  return authTokenProvider();
}

async function refreshAccessToken() {
  if (!authRefreshHandler) return null;
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        return await authRefreshHandler();
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

export async function request(method, path, options = {}) {
  const {
    headers = {},
    params,
    body,
    authToken,
    signal,
    returnMeta = false,
    logRequestId = false,
    skipAuth = false,
    skipAuthRefresh = false,
    _retry = false,
  } = options;

  const baseUrl = joinUrl(API_BASE_URL, path);
  const query = buildQuery(params);
  const url = query && baseUrl.includes('?')
    ? `${baseUrl}&${query.slice(1)}`
    : `${baseUrl}${query}`;
  const requestHeaders = {
    ...DEFAULT_HEADERS,
    'x-devin-repo': FRONTEND_REPO,
    'x-correlation-id': generateCorrelationId(),
    ...headers,
  };

  const resolvedAuthToken = await resolveAuthToken({ authToken, skipAuth });

  if (resolvedAuthToken) {
    requestHeaders.Authorization = `Bearer ${resolvedAuthToken}`;
  }

  const fetchOptions = {
    method,
    headers: requestHeaders,
    signal,
  };

  if (body !== undefined) {
    const isJsonBody = isPlainObject(body) || Array.isArray(body);
    if (isJsonBody) {
      requestHeaders['Content-Type'] = 'application/json';
      fetchOptions.body = JSON.stringify(body);
    } else {
      fetchOptions.body = body;
    }
  }

  let response;
  try {
    response = await fetch(url, fetchOptions);
  } catch (error) {
    console.error('[api] request failed:', error);
    throw error;
  }

  const requestId = getRequestId(response.headers);

  if (requestId && logRequestId) {
    console.info(`[api] request_id=${requestId}`);
  }

  const data = await parseResponseBody(response);

  if (!response.ok) {
    const apiError = buildApiError({
      status: response.status,
      statusText: response.statusText,
      data,
      requestId,
    });

    if (apiError.status === 401 && !skipAuthRefresh && !_retry) {
      try {
        const refreshedToken = await refreshAccessToken();
        if (refreshedToken) {
          return request(method, path, {
            ...options,
            authToken: refreshedToken,
            skipAuthRefresh: true,
            _retry: true,
          });
        }
      } catch (refreshError) {
        console.error('[api] token refresh failed:', refreshError);
      }
    }

    throw apiError;
  }

  if (returnMeta) {
    return {
      data,
      requestId,
      status: response.status,
    };
  }

  return data;
}

export const apiClient = {
  get: (path, options) => request('GET', path, options),
  post: (path, body, options = {}) => request('POST', path, { ...options, body }),
  put: (path, body, options = {}) => request('PUT', path, { ...options, body }),
  patch: (path, body, options = {}) => request('PATCH', path, { ...options, body }),
  delete: (path, options) => request('DELETE', path, options),
};
